import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import AccessibilityToolbar from './components/AccessibilityToolbar';
import AssistantWidget from './components/AssistantWidget';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Each page is now loaded on demand instead of all being bundled into
// one giant file. A visitor on /login only downloads the Login page's
// code, not the entire chart-explorer/admin-dashboard code too.
const Home = lazy(() => import('./pages/Home'));
const ResearchList = lazy(() => import('./pages/ResearchList'));
const ResearchDetail = lazy(() => import('./pages/ResearchDetail'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Register = lazy(() => import('./pages/Register'));
const Profile = lazy(() => import('./pages/Profile'));
const Favorites = lazy(() => import('./pages/Favorites'));
const EnhancedCategories = lazy(() => import('./pages/EnhancedCategories'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OpenBook = lazy(() => import('./pages/OpenBook'));
const EmbedChart = lazy(() => import('./pages/EmbedChart'));
const About = lazy(() => import('./pages/About'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile'));
const PublicPage = lazy(() => import('./pages/PublicPage'));
const ArticleJournalList = lazy(() => import('./pages/ArticleJournalList'));
const ArticleJournalDetail = lazy(() => import('./pages/ArticleJournalDetail'));
const CurrentAffairList = lazy(() => import('./pages/CurrentAffairList'));
const CurrentAffairDetail = lazy(() => import('./pages/CurrentAffairDetail'));
const Donate = lazy(() => import('./pages/Donate'));
const DownloadData = lazy(() => import('./pages/DownloadData'));

// Shown briefly while a page's code is being downloaded.
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-acar-navy-600"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-acar-navy-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Everything that needs router context (useLocation) lives here, inside
// <Router>. Embed routes (meant to be loaded in an external site's
// <iframe>) skip the navbar/footer/toolbar/widget entirely -- just the
// chart, nothing else, since none of that site chrome makes sense
// inside someone else's page.
const AppLayout = () => {
  const location = useLocation();
  const { settings } = useSiteSettings();
  const isEmbed = location.pathname.startsWith('/embed');
  // The donation flow gets a minimal header instead of the full nav --
  // research on donation page conversion consistently finds that
  // removing the standard site navigation during checkout meaningfully
  // reduces abandonment, since every nav link is a potential exit once
  // someone's decided to give. Footer stays as-is since it's below the
  // fold and doesn't interrupt the flow the same way.
  const isDonate = location.pathname === '/donate';

  const routes = (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Embed routes -- standalone, no site chrome */}
        <Route path="/embed/dataset/:id" element={<EmbedChart />} />

        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/research" element={<ResearchList />} />
        <Route path="/research/:id" element={<ResearchDetail />} />

        <Route path="/datasets" element={<EnhancedCategories />} />
        <Route path="/open-book" element={<OpenBook />} />
        <Route path="/about" element={<About />} />
        <Route path="/company-profile" element={<CompanyProfile />} />
        <Route path="/pages/:slug" element={<PublicPage />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/download-data" element={<DownloadData />} />
        <Route
          path="/publications/articles-journals"
          element={<ArticleJournalList />}
        />
        <Route
          path="/publications/articles-journals/:id"
          element={<ArticleJournalDetail />}
        />
        <Route
          path="/publications/current-affairs"
          element={<CurrentAffairList />}
        />
        <Route
          path="/publications/current-affairs/:id"
          element={<CurrentAffairDetail />}
        />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />

        {/* Protected Routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/favorites"
          element={
            <ProtectedRoute>
              <Favorites />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard/*"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );

  if (isEmbed) {
    // No Navbar, Footer, AccessibilityToolbar, or AssistantWidget --
    // an embed exists inside someone else's page, so none of the host
    // site's own navigation/chrome belongs here.
    return <ErrorBoundary>{routes}</ErrorBoundary>;
  }

  if (isDonate) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="bg-acar-navy-800 py-3">
          <div className="container mx-auto px-4">
            <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt={settings.site_name} className="h-14 w-auto object-contain" />
              ) : (
                <span className="text-white font-bold text-lg">{settings.site_name || 'ACAR'}</span>
              )}
            </Link>
          </div>
        </div>
        <main className="flex-grow">
          <ErrorBoundary>{routes}</ErrorBoundary>
        </main>
        <Footer />
        <AccessibilityToolbar />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        <ErrorBoundary>{routes}</ErrorBoundary>
      </main>
      <Footer />
      <AccessibilityToolbar />
      <AssistantWidget />
    </div>
  );
};

function App() {
  return (
    <SiteSettingsProvider>
      <LanguageProvider>
        <Router>
          <AuthProvider>
            <AppLayout />
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </SiteSettingsProvider>
  );
}

const NotFound = () => (
  <div className="container mx-auto px-4 py-16 text-center">
    <h1 className="text-6xl font-bold text-gray-800 mb-4">404</h1>
    <p className="text-xl text-gray-600 mb-8">Page not found</p>
    <a
      href="/"
      className="inline-block px-6 py-3 bg-acar-navy-600 text-white rounded-lg hover:bg-acar-navy-700 transition"
    >
      Go Home
    </a>
  </div>
);

export default App;
